import React from 'react'
import styled from '@emotion/styled'
import { Card, CardActionArea, CardContent } from '@mui/material'
import { ActionText } from './ActionText'
import { MUIRouterLink } from '@edge/spark/ui'
import { WidgetCardHeader } from './WidgetCardHeader'
import {
    type CardFormat,
    CardMode,
    CardType,
    CardViewMode,
    GenericWidgetConfig,
    useIsEditingLayouts,
} from '@edge/spark/io-dashboards'

const randNum = (min: number, max: number): number => {
    return Math.random() * (max - min) + min
}

interface Rand {
    animationprops: RandomSeed
}

interface RandomSeed {
    duration: number
    tfX: number
    tfY: number
    rotNeg: number
    rotPos: number
    enabled?: boolean
}

const getNumbers = (): RandomSeed => {
    return {
        duration: randNum(0.28, 0.36),
        tfX: randNum(10, 30),
        tfY: randNum(10, 30),
        rotNeg: randNum(-0.4, -0.2),
        rotPos: randNum(0.4, 0.2),
    }
}

const AnimatedCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'animationprops',
})<Rand>`
    margin: 0;
    padding: 8px;
    height: 225px;
    overflow-x: clip;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    scrollbar-width: thin;

    ${({ animationprops: { duration, rotNeg, rotPos, enabled } }: Rand) =>
        enabled
            ? `
animation: keyframes1 ${duration}s infinite;
animation-timing-function: ease-in-out;
@keyframes keyframes1 {
    0% {
        transform: rotate(${rotNeg}deg);
    }
    50% {
        transform: rotate(${rotPos}deg);
    }
}

`
            : ''}
`

interface Props {
    cardType?: CardType
    cardHeaderText?: string
    action?: {
        link: string
        text: string
    }
    cardConfig?: GenericWidgetConfig
    handleConfig?: (widgetId: string) => void
    handleDisplayDetails?: (widgetId: string) => void
    widgetId: string
    cardViewMode?: CardViewMode
    cardSize?: number
    cardFormat?: CardFormat
    children: React.ReactNode
    loading?: boolean
    readonly: boolean
}

export const WidgetContainer: React.FC<Props> = ({
    cardType = CardType.CUSTOM,
    cardHeaderText = cardType !== CardType.CUSTOM ? cardType.valueOf() : 'Custom',
    action,
    handleConfig,
    handleDisplayDetails,
    widgetId,
    cardViewMode = CardViewMode.DASHBOARD,
    // cardSize = 1,
    // cardFormat = CardFormat.LANDSCAPE,
    children,
    loading,
    readonly,
}: Props) => {
    const isEditingLayouts = useIsEditingLayouts()

    const backgroundColor = CardMode.LIGHT.valueOf()

    const numbers = React.useMemo(getNumbers, [])

    const handleCardClick = (): void => {
        if (handleDisplayDetails) {
            handleDisplayDetails(widgetId)
        }
    }

    const href = (!isEditingLayouts && action?.link) || undefined

    return (
        <CardActionArea component="div">
            <AnimatedCard
                animationprops={{ ...numbers, enabled: isEditingLayouts }}
                sx={{}}
                style={{ backgroundColor }}
                raised={cardViewMode === CardViewMode.CONFIGURATION}
                elevation={cardViewMode === CardViewMode.CONFIGURATION ? 3 : 2}
                onClick={
                    cardViewMode === CardViewMode.DASHBOARD || cardViewMode === CardViewMode.PANEL
                        ? (evt): void => {
                              if (isEditingLayouts) {
                                  evt.preventDefault()
                                  handleCardClick()
                              }
                          }
                        : undefined
                }
            >
                <WidgetCardHeader
                    href={href}
                    cardHeaderText={cardHeaderText}
                    loading={loading}
                    handleConfig={handleConfig}
                    widgetId={widgetId}
                    cardViewMode={cardViewMode}
                    readonly={readonly}
                />
                <MUIRouterLink href={href} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ p: 0, flex: 1 }}>{children}</CardContent>
                    <ActionText>{action?.text}</ActionText>
                </MUIRouterLink>
            </AnimatedCard>
        </CardActionArea>
    )
}
